import type { QueryClient } from '@tanstack/react-query'
import { notFound } from '@tanstack/react-router'
import type { QueryOptionsResult } from '../generated/entity-config'
import {
	parseEntityId,
	resolveEntity,
	resolveEntityTab,
	resolveSettingsSection,
} from './entity-route-resolver'
import {
	type EntityListSearch,
	type EntityTabSearch,
	getRelationSearch,
} from './entity-search'

export type EntityLoaderContext = {
	queryClient: QueryClient
}

export type EntityListQueryResult = {
	data?: unknown[]
	total?: number
}

export type EntityRelationQueryVariant = 'assigned' | 'available'

type EntityRouteEntity = ReturnType<typeof resolveEntity>
type EntityPathParams = Record<string, number>
const relationQueryStaleTime = 1_000

function MissingAssignedEndpointError(
	EntityKey: string,
	relationKey: string,
): Error {
	return new Error(
		`Missing assigned endpoint for relation "${relationKey}" in Entity "${EntityKey}".`,
	)
}

function MissingAvailableEndpointError(
	EntityKey: string,
	relationKey: string,
): Error {
	return new Error(
		`Missing available endpoint for relation "${relationKey}" in Entity "${EntityKey}".`,
	)
}

function resolveEntityListRouteQuery(args: {
	params: { entity: string }
	search: EntityListSearch
}) {
	const Entity = resolveEntity(args.params.entity)
	const endpoint = Entity.endpoints.list
	if (endpoint?.method !== 'GET') throw notFound()
	const options = endpoint.queryOptions({ queryParams: args.search })
	return { EntityKey: Entity.key, options }
}

function getDisabledQueryOptions<TData = EntityListQueryResult>(
	queryKey: readonly unknown[],
): QueryOptionsResult<TData> {
	return {
		queryKey,
		enabled: false,
	}
}

function withRelationQueryStaleTime<TData>(
	options: QueryOptionsResult<TData>,
): QueryOptionsResult<TData> {
	return {
		...options,
		staleTime: options.staleTime ?? relationQueryStaleTime,
	}
}

function getEntityPathParams(args: {
	params: { entity: string; entityId: string }
}) {
	const Entity = resolveEntity(args.params.entity)
	const entityId = parseEntityId(args.params.entityId)
	const ownerParam = Entity.entity.idParam
	if (!ownerParam) throw notFound()
	return { Entity, entityId, pathParams: { [ownerParam]: entityId } }
}

export function getEntityListQueryOptions(args: {
	params: { entity: string }
	search: EntityListSearch
}): QueryOptionsResult<EntityListQueryResult> {
	return resolveEntityListRouteQuery(args)
		.options as QueryOptionsResult<EntityListQueryResult>
}

export function getEntityDetailQueryOptions(args: {
	params: { entity: string; entityId: string }
	search?: EntityListSearch
}): QueryOptionsResult<unknown> {
	const { Entity, entityId, pathParams } = getEntityPathParams(args)
	const detail = Entity.endpoints.detail
	if (detail?.method === 'GET') {
		return detail.queryOptions({ pathParams })
	}

	const list = Entity.endpoints.list
	if (list?.method === 'GET') {
		return list.queryOptions({
			queryParams: args.search ?? { skip: 0, take: 500 },
		})
	}

	return getDisabledQueryOptions<unknown>([
		'entity-config',
		Entity.key,
		entityId,
		'detail',
		'missing',
	])
}

export function getEntityRelationQueryOptions(args: {
	params: { entity: string; entityId: string; tab: string }
	search: EntityTabSearch
	variant: EntityRelationQueryVariant
}): QueryOptionsResult<EntityListQueryResult> {
	const { Entity, entityId, pathParams } = getEntityPathParams(args)
	const tab = resolveEntityTab(Entity, args.params.tab)
	if (tab.kind !== 'relation') throw notFound()

	const relation = Entity.relations?.[tab.relationKey]
	const endpoint =
		args.variant === 'assigned'
			? relation?.endpoints.assigned
			: relation?.endpoints.available

	if (endpoint?.method !== 'GET') {
		if (args.variant === 'available' || args.variant === 'assigned') {
			return getDisabledQueryOptions([
				'entity-config',
				Entity.key,
				entityId,
				tab.key,
				args.variant,
				'missing',
			])
		}
		throw args.variant === 'assigned'
			? MissingAssignedEndpointError(Entity.key, tab.relationKey)
			: MissingAvailableEndpointError(Entity.key, tab.relationKey)
	}

	return withRelationQueryStaleTime(
		endpoint.queryOptions({
			pathParams,
			queryParams: getRelationSearch(
				args.search,
				args.variant === 'assigned' ? 'assigned' : 'assignable',
			),
		}),
	) as QueryOptionsResult<EntityListQueryResult>
}

export async function loadEntityListRoute(args: {
	params: { entity: string }
	search: EntityListSearch
	context: EntityLoaderContext
}) {
	const { EntityKey, options } = resolveEntityListRouteQuery(args)
	await args.context.queryClient.prefetchQuery(options)
	return { EntityKey, queryKey: options.queryKey }
}

async function loadOverviewTabRoute(args: {
	Entity: EntityRouteEntity
	entityId: number
	tabKey: string
	pathParams: EntityPathParams
	context: EntityLoaderContext
}) {
	const endpoint = args.Entity.endpoints.detail
	if (endpoint?.method === 'GET') {
		const options = endpoint.queryOptions({ pathParams: args.pathParams })
		await args.context.queryClient.ensureQueryData(options)
		return {
			EntityKey: args.Entity.key,
			entityId: args.entityId,
			tabKey: args.tabKey,
			queryKey: options.queryKey,
		}
	}
	return {
		EntityKey: args.Entity.key,
		entityId: args.entityId,
		tabKey: args.tabKey,
	}
}

async function loadRelationTabRoute(args: {
	Entity: EntityRouteEntity
	entityId: number
	tabKey: string
	relationKey: string
	pathParams: EntityPathParams
	search: EntityTabSearch
	context: EntityLoaderContext
}) {
	const relation = args.Entity.relations?.[args.relationKey]
	const assigned = relation?.endpoints.assigned
	const available = relation?.endpoints.available
	if (assigned && assigned.method !== 'GET')
		throw MissingAssignedEndpointError(args.Entity.key, args.relationKey)
	if (available && available.method !== 'GET')
		throw MissingAvailableEndpointError(args.Entity.key, args.relationKey)
	if (assigned?.method !== 'GET' && available?.method !== 'GET') {
		throw MissingAvailableEndpointError(args.Entity.key, args.relationKey)
	}
	const assignedSearch = getRelationSearch(args.search, 'assigned')
	const availableSearch = getRelationSearch(args.search, 'assignable')
	const assignedOptions =
		assigned?.method === 'GET'
			? withRelationQueryStaleTime(
					assigned.queryOptions({
						pathParams: args.pathParams,
						queryParams: assignedSearch,
					}),
				)
			: undefined
	const availableOptions =
		available?.method === 'GET'
			? withRelationQueryStaleTime(
					available.queryOptions({
						pathParams: args.pathParams,
						queryParams: availableSearch,
					}),
				)
			: undefined
	await Promise.all([
		assignedOptions
			? args.context.queryClient.ensureQueryData(assignedOptions)
			: Promise.resolve(),
		availableOptions
			? args.context.queryClient.prefetchQuery(availableOptions)
			: Promise.resolve(),
	])
	return {
		EntityKey: args.Entity.key,
		entityId: args.entityId,
		tabKey: args.tabKey,
		relationKey: args.relationKey,
		assignedQueryKey: assignedOptions?.queryKey,
		availableQueryKey: availableOptions?.queryKey,
	}
}

async function loadSettingsTabRoute(args: {
	Entity: EntityRouteEntity
	entityId: number
	tabKey: string
	pathParams: EntityPathParams
	search: EntityTabSearch
	context: EntityLoaderContext
}) {
	if (!args.Entity.settings) throw notFound()
	const section = resolveSettingsSection(
		args.Entity.settings,
		args.search.section,
	)
	const load = section.endpoints.load
	if (load?.method !== 'GET') throw notFound()
	const options = load.queryOptions({ pathParams: args.pathParams })
	await args.context.queryClient.ensureQueryData(options)
	return {
		EntityKey: args.Entity.key,
		entityId: args.entityId,
		tabKey: args.tabKey,
		sectionKey: section.key,
		queryKey: options.queryKey,
	}
}

export async function loadEntityTabRoute(args: {
	params: { entity: string; entityId: string; tab: string }
	search: EntityTabSearch
	context: EntityLoaderContext
}) {
	const { Entity, entityId, pathParams } = getEntityPathParams(args)
	const tab = resolveEntityTab(Entity, args.params.tab)

	if (tab.kind === 'overview') {
		return loadOverviewTabRoute({
			Entity,
			entityId,
			tabKey: tab.key,
			pathParams,
			context: args.context,
		})
	}

	if (tab.kind === 'relation') {
		return loadRelationTabRoute({
			Entity,
			entityId,
			tabKey: tab.key,
			relationKey: tab.relationKey,
			pathParams,
			search: args.search,
			context: args.context,
		})
	}

	if (tab.kind === 'settings') {
		return loadSettingsTabRoute({
			Entity,
			entityId,
			tabKey: tab.key,
			pathParams,
			search: args.search,
			context: args.context,
		})
	}

	throw notFound()
}
